import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { selectSidebarCollapsed } from "../store/slices/app/appSelector";
import Sidebar from "../components/sidebar/Sidebar";
import { getPlatformDisplayName } from "../utils/iconUtils";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useGetMergedCampaignMetricsQuery } from "../store/apis/internalCampaignsMergeApi";
import { setToastMessage } from "../store/slices/app/appSlice";
import NatAdsMergeCampaignDetails from "../components/campaign/NatAdsMergeCampaignDetails";
import Dv360MergeCampaignDetails from "../components/campaign/Dv360MergeCampaignDetails";
import SpotifyMergeCampaignDetails from "../components/campaign/SpotifyMergeCampaignDetails";
import HivestackMergeCampaignDetails from "../components/campaign/HivestackMergeCampaignDetails";
import AmazonDspMergeCampaignDetails from "../components/campaign/AmazonDspMergeCampaignDetails";
import CTVMergeCampaignDetails from "../components/campaign/CTVMergeCampaignDetails";
import GoogleAdsMergeCampaignDetails from "../components/campaign/GoogleAdsMergeCampaignDetails";
import SocialMergeCampaignDetails, {
  SocialNetwork,
} from "../components/campaign/SocialMergeCampaignDetails";
import PageBreadcrumbs from "../components/layout/PageBreadcrumbs";

const MergedCampaignDetails: React.FC = () => {
  // State for date filters
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [hasInitializedDates, setHasInitializedDates] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { mergedCampaignId, parentCampaignId } = useParams<{
    mergedCampaignId: string;
    parentCampaignId: string;
  }>();
  const sidebarCollapsed = useSelector(selectSidebarCollapsed);
  const {
    data: mergedCampaignData,
    error: mergedCampaignError,
    isLoading: isLoadingMergedCampaign,
  } = useGetMergedCampaignMetricsQuery(
    {
      mergedCampaignId: mergedCampaignId || "",
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
    },
    {
      skip: !mergedCampaignId,
      refetchOnMountOrArgChange: true,
    }
  );
  const platform = mergedCampaignData?.campaignPlatform;
  const SOCIAL_NETWORKS: SocialNetwork[] = ["meta", "tiktok", "reddit"];
  const socialNetwork = SOCIAL_NETWORKS.find(
    (network) => network === platform?.toLowerCase()
  );

  // Effect to handle date filter changes
  useEffect(() => {
    // The API query will automatically refetch when the parameters change
    // This is handled by the RTK Query dependency array
  }, [startDate, endDate]);

  // Log errors for debugging
  useEffect(() => {
    if (mergedCampaignError) {
      dispatch(
        setToastMessage({
          message: t("campaigns.campaignDetailError", {
            defaultValue: "Failed to load campaign details",
          }),
          severity: "error",
        })
      );
    }
  }, [dispatch, mergedCampaignError, t]);

  const handleBack = () => {
    // Navigate back to campaigns page with the parent campaign ID as state
    // This will be used to reselect the parent campaign
    navigate("/campaigns", { state: { selectedCampaignId: parentCampaignId } });
  };

  const handleMetricsStartDateChange = useCallback((value: string) => {
    setStartDate(value);
    if (!value) {
      setEndDate("");
    }
  }, []);

  const handleMetricsEndDateChange = useCallback((value: string) => {
    setEndDate(value);
  }, []);

  const handleClearMetricsDates = useCallback(() => {
    setStartDate("");
    setEndDate("");
  }, []);

  const formatDateForInput = useCallback(
    (dateString: string | undefined): string => {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toISOString().split("T")[0];
    },
    []
  );

  // Effect to initialize date filters with campaign dates when data is loaded
  useEffect(() => {
    if (
      mergedCampaignData &&
      !hasInitializedDates &&
      mergedCampaignData.startDate &&
      mergedCampaignData.endDate
    ) {
      setStartDate(formatDateForInput(mergedCampaignData.startDate));
      setEndDate(formatDateForInput(mergedCampaignData.endDate));
      setHasInitializedDates(true);
    }
  }, [mergedCampaignData, hasInitializedDates, formatDateForInput]);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Sidebar />
      <Box
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          transition: "margin-left 0.3s ease",
          marginLeft: sidebarCollapsed ? "80px" : "250px",
          display: "flex",
          flexDirection: "column",
          width: { xs: "calc(100% - 80px)", sm: "auto" },
          maxWidth: "100%",
          boxSizing: "border-box",
          overflowX: "hidden",
          minHeight: "100vh",
        }}
      >
        <PageBreadcrumbs />

        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 0 },
            mb: 3,
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            onClick={handleBack}
            sx={{
              mr: 2,
              borderRadius: 50,
              textTransform: "none",
              fontFamily: "Nunito",
              borderColor: theme.palette.grey[200],
              color: theme.palette.grey[700],
              "&:hover": {
                borderColor: theme.palette.primary.main,
                backgroundColor: "transparent",
              },
            }}
          >
            {t("campaigns.back")}
          </Button>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {mergedCampaignData?.name || ""}
            </Typography>
            {mergedCampaignData?.campaignPlatform && (
              <Box
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: "primary.light",
                  color: "primary.contrastText",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                {getPlatformDisplayName(mergedCampaignData?.campaignPlatform)}
              </Box>
            )}
          </Box>
        </Box>
        {platform?.toLowerCase() === "natads" && (
          <NatAdsMergeCampaignDetails
            isLoadingMergedCampaign={isLoadingMergedCampaign}
            mergedCampaignId={mergedCampaignId}
            mergedCampaignData={mergedCampaignData}
          />
        )}
        {socialNetwork && (
          <SocialMergeCampaignDetails
            network={socialNetwork}
            isLoadingMergedCampaign={isLoadingMergedCampaign}
            mergedCampaignId={mergedCampaignId}
            mergedCampaignData={mergedCampaignData}
            metricsStartDate={startDate}
            metricsEndDate={endDate}
            onMetricsStartDateChange={handleMetricsStartDateChange}
            onMetricsEndDateChange={handleMetricsEndDateChange}
            onClearMetricsDateFilters={handleClearMetricsDates}
          />
        )}
        {platform?.toLowerCase() === "beapolo" && (
          <Dv360MergeCampaignDetails
            isLoadingMergedCampaign={isLoadingMergedCampaign}
            mergedCampaignId={mergedCampaignId}
            mergedCampaignData={mergedCampaignData}
            metricsStartDate={startDate}
            metricsEndDate={endDate}
            onMetricsStartDateChange={handleMetricsStartDateChange}
            onMetricsEndDateChange={handleMetricsEndDateChange}
            onClearMetricsDateFilters={handleClearMetricsDates}
          />
        )}
        {(platform?.toLowerCase() === "spotify" ||
          platform?.toLowerCase() === "spotify_ads") && (
          <SpotifyMergeCampaignDetails
            isLoadingMergedCampaign={isLoadingMergedCampaign}
            mergedCampaignId={mergedCampaignId}
            mergedCampaignData={mergedCampaignData}
            metricsStartDate={startDate}
            metricsEndDate={endDate}
            onMetricsStartDateChange={handleMetricsStartDateChange}
            onMetricsEndDateChange={handleMetricsEndDateChange}
            onClearMetricsDateFilters={handleClearMetricsDates}
          />
        )}
        {platform?.toLowerCase() === "hivestack" && (
          <HivestackMergeCampaignDetails
            isLoadingMergedCampaign={isLoadingMergedCampaign}
            mergedCampaignId={mergedCampaignId}
            mergedCampaignData={mergedCampaignData}
            metricsStartDate={startDate}
            metricsEndDate={endDate}
            onMetricsStartDateChange={handleMetricsStartDateChange}
            onMetricsEndDateChange={handleMetricsEndDateChange}
            onClearMetricsDateFilters={handleClearMetricsDates}
          />
        )}
        {(platform?.toLowerCase() === "amazon_dsp" ||
          platform?.toLowerCase() === "amazon") && (
          <AmazonDspMergeCampaignDetails
            isLoadingMergedCampaign={isLoadingMergedCampaign}
            mergedCampaignId={mergedCampaignId}
            mergedCampaignData={mergedCampaignData}
            metricsStartDate={startDate}
            metricsEndDate={endDate}
            onMetricsStartDateChange={handleMetricsStartDateChange}
            onMetricsEndDateChange={handleMetricsEndDateChange}
            onClearMetricsDateFilters={handleClearMetricsDates}
          />
        )}
        {platform?.toLowerCase() === "googleads" && (
          <GoogleAdsMergeCampaignDetails
            isLoadingMergedCampaign={isLoadingMergedCampaign}
            mergedCampaignId={mergedCampaignId}
            mergedCampaignData={mergedCampaignData}
            metricsStartDate={startDate}
            metricsEndDate={endDate}
            onMetricsStartDateChange={handleMetricsStartDateChange}
            onMetricsEndDateChange={handleMetricsEndDateChange}
            onClearMetricsDateFilters={handleClearMetricsDates}
          />
        )}
        {platform?.toLowerCase() === "ctv" && (
          <CTVMergeCampaignDetails
            isLoadingMergedCampaign={isLoadingMergedCampaign}
            mergedCampaignId={mergedCampaignId}
            mergedCampaignData={mergedCampaignData}
            metricsStartDate={startDate}
            metricsEndDate={endDate}
            onMetricsStartDateChange={handleMetricsStartDateChange}
            onMetricsEndDateChange={handleMetricsEndDateChange}
            onClearMetricsDateFilters={handleClearMetricsDates}
          />
        )}
      </Box>
    </Box>
  );
};

export default MergedCampaignDetails;
